/**
 * <Section> — major page section.
 *
 * Composes <Heading level={2}> + body. Use for top-level groupings within
 * a page (e.g., "Active work orders", "Open tasks"). Cards within the
 * section use <Heading level={3}> via the Card component.
 *
 * The section title renders as semantic <h2> by default. Override with
 * `as="h3"` for places that need an <h3> structurally but the visual
 * weight of an H2 — that's the documented escape hatch from
 * `02-typography.md`.
 */

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../utils/cn';
import { Heading, type HeadingLevel } from './Heading';
import { Text } from './Text';

export interface SectionProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Section title. Optional — sections without titles are valid. */
  title?: ReactNode;
  /** Sub-line under the title. */
  description?: ReactNode;
  /** Right-aligned actions. */
  actions?: ReactNode;
  /**
   * Logical heading level the title renders at. Defaults to `2`.
   * Use `3` when the section is nested inside another <Section>.
   */
  level?: HeadingLevel;
  children: ReactNode;
}

export const Section = forwardRef<HTMLElement, SectionProps>(function Section(
  { title, description, actions, level = 2, className, children, ...rest },
  ref,
) {
  return (
    <section ref={ref} className={cn('flex flex-col gap-3', className)} {...rest}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            {title && <Heading level={level}>{title}</Heading>}
            {description && (
              <Text variant="body-sm" tone="muted">
                {description}
              </Text>
            )}
          </div>
          {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
});

Section.displayName = 'Section';

export default Section;
